import { COLORS, COLOR_LIST, ColorTrait, ColorFamily } from "./colors";
import { shuffle, pickRandom, randomInt } from "@/lib/random";

export type QuizType = 'personality' | 'decision';

export type Round = {
  id: string;
  prompt: string;
  options: string[]; // array of color IDs
  kind?: 'pick' | 'avoid';
  stepKind?: DecisionStepKind;
};

// ---------------------------------------------------------------------------
// Personality quiz: always exactly 7 rounds (6 "pick" + 1 "avoid") with 4
// color choices each, but every round is drawn from a much larger bank of
// prompts than we ever show at once — so the 7 questions themselves are
// different practically every playthrough, not just the colors within them.
// ---------------------------------------------------------------------------

const PERSONALITY_ROUND_COUNT = 6; // + 1 "avoid" round = 7 total
const OPTIONS_PER_ROUND = 4;

// ---------------------------------------------------------------------------
// Prompt bank generation
//
// To keep a huge, varied pool of questions without hand-writing every single
// sentence, each prompt is built by pairing a subject phrase (what the
// question is actually asking about) with a sentence template (how it's
// phrased). Every subject is combined with two different templates, so the
// same idea shows up worded two different ways across the pool — multiplying
// variety while keeping every sentence grammatical and legible.
// ---------------------------------------------------------------------------

function expandPrompts(subjects: string[], templates: ((s: string) => string)[]): string[] {
  const out: string[] = [];
  subjects.forEach((subject, i) => {
    const t1 = templates[i % templates.length];
    const t2 = templates[(i + 3) % templates.length];
    out.push(t1(subject));
    if (t2 !== t1) out.push(t2(subject));
  });
  return out;
}

const PICK_TEMPLATES: ((s: string) => string)[] = [
  (s) => `Which color feels like ${s}?`,
  (s) => `Which color represents ${s}?`,
  (s) => `Choose the color that feels like ${s}.`,
  (s) => `Which of these feels like ${s}?`,
  (s) => `Which color would you pick for ${s}?`,
  (s) => `Which color captures ${s}?`,
  (s) => `If you had to choose, which color feels closest to ${s}?`,
  (s) => `Pick the color that reminds you of ${s}.`,
];

const PICK_SUBJECTS: string[] = [
  'the energy you bring into a room', 'a deep breath', 'a place you want to be', 'motion',
  'a shield', 'the version of you other people see', 'a day you needed courage', 'a memory you keep returning to',
  'the room you think most clearly in', 'a secret your instinct would trust', 'the sound of your own thoughts', 'a promise you intend to keep',
  'your childhood home', 'the way you love people', 'the version of you at your best', 'your current mood',
  'a door you have not opened yet', 'the way you handle conflict', 'a fresh start', 'your idea of freedom',
  'the friend you needed most this year', 'a hard day', 'the way your mind moves when no one is watching', 'a compliment you would actually believe',
  'the last big risk you took', 'your inner voice', 'the texture of a perfect ordinary day', 'something you are still learning to trust',
  'the theme of your next chapter', 'a place that feels like home', 'the sound of rain at night', 'a stranger\'s kindness',
  'your first big dream', 'the version of you five years from now', 'the smell of a new beginning', 'a quiet Sunday morning',
  'the feeling of being understood', 'a song stuck in your head', 'the calm after a storm', 'a promise you never broke',
  'the way you celebrate good news', 'a decision you are proud of', 'the part of you that shows up in a crisis', 'your favorite kind of silence',
  'the way you comfort someone else', 'a lesson you learned the hard way', 'the feeling of finally letting go', 'your idea of a perfect evening',
  'the way you greet a new idea', 'a truth you are not ready to say out loud',
];

const PERSONALITY_PICK_PROMPTS = expandPrompts(PICK_SUBJECTS, PICK_TEMPLATES);

const AVOID_TEMPLATES: ((s: string) => string)[] = [
  (s) => `Which of these feels the most like ${s}?`,
  (s) => `Which color represents ${s}?`,
  (s) => `Choose the color that feels like ${s}.`,
  (s) => `Which of these feels closest to ${s}?`,
];

const AVOID_SUBJECTS: string[] = [
  'a habit you are trying to outgrow', 'a version of yourself you no longer want', 'an old fear you are ready to release', 'the most exhausting thing to sit with',
  'a chapter you are ready to close', 'the heaviest thing to carry', 'a comparison you keep making', 'a doubt that keeps returning',
  'an apology you are still waiting for', 'a mistake you have already forgiven yourself for', 'the noise you want to quiet', 'a story you are ready to rewrite',
  'a weight you no longer need', 'an excuse you keep making', 'a fear that no longer serves you', 'the version of you that people outgrew with you',
  'a grudge you would rather put down', 'the part of your day you dread most', 'a worry that rarely comes true', 'the color you want to leave behind',
];

const PERSONALITY_AVOID_PROMPTS = expandPrompts(AVOID_SUBJECTS, AVOID_TEMPLATES);

function generateRoundsFromPrompts(prompts: string[], usedPrefix: string): Round[] {
  return prompts.map((prompt, i) => {
    const options = pickRandom(COLOR_LIST, OPTIONS_PER_ROUND).map(c => c.id);
    return { id: `${usedPrefix}${i}`, prompt, options, kind: 'pick' as const };
  });
}

function buildPersonalityRounds(): Round[] {
  const pickPrompts = pickRandom(PERSONALITY_PICK_PROMPTS, PERSONALITY_ROUND_COUNT);
  const avoidPrompt = pickRandom(PERSONALITY_AVOID_PROMPTS, 1)[0];

  const pickRounds = generateRoundsFromPrompts(pickPrompts, 'p');
  const avoidRound: Round = {
    id: 'p-avoid',
    prompt: avoidPrompt,
    options: pickRandom(COLOR_LIST, OPTIONS_PER_ROUND).map(c => c.id),
    kind: 'avoid',
  };

  // Insert the "avoid" round anywhere in the sequence (including first or
  // last) so the shape of the quiz itself is unpredictable, not just the
  // colors within each round.
  const withAvoid = [...pickRounds];
  const insertAt = randomInt(0, pickRounds.length);
  withAvoid.splice(insertAt, 0, avoidRound);

  return withAvoid;
}

function signatureOf(rounds: Round[]): string {
  return rounds.map(r => `${r.prompt}::${r.options.join(',')}`).join('|');
}

// Keep the previously served round set in memory (per browser session) so a
// visitor can never be handed the exact same quiz twice in a row, even if
// the shuffle happened to land on a duplicate by chance.
let lastPersonalitySignature: string | null = null;
let lastDecisionSignature: string | null = null;

export function generatePersonalityRounds(): Round[] {
  let rounds = buildPersonalityRounds();
  let attempts = 0;
  while (signatureOf(rounds) === lastPersonalitySignature && attempts < 5) {
    rounds = buildPersonalityRounds();
    attempts += 1;
  }
  lastPersonalitySignature = signatureOf(rounds);
  return rounds;
}

// ---------------------------------------------------------------------------
// Decision quiz: five fixed *meanings* (conflict, safe path, risk, external
// pressure, true desire) but each has several prompt phrasings, is shown in a
// shuffled order, and draws a random slice of colors — so the reading always
// stays consistent, while the experience never repeats.
// ---------------------------------------------------------------------------

export type DecisionStepKind = 'conflict' | 'safe' | 'risk' | 'pressure' | 'desire';

const DECISION_STEP_LABELS: Record<DecisionStepKind, string> = {
  conflict: 'The Core Conflict',
  safe: 'The Safe Path',
  risk: 'The Risk',
  pressure: 'External Pressure',
  desire: 'Your True Desire',
};

const DECISION_PROMPT_BANK: Record<DecisionStepKind, string[]> = {
  conflict: [
    'Bring the decision to mind. Which color represents the conflict itself?',
    'Which color captures the tension at the center of this decision?',
    'If this decision were a color, tangled and unresolved, which would it be?',
    'Which color feels like the knot you have not untangled yet?',
    'Which color represents the two sides pulling at you right now?',
    'Which color feels like the question you keep circling back to?',
  ],
  safe: [
    'Which color feels like the safest choice you could make?',
    'Which color represents the option that keeps everything steady?',
    'Which color feels like the path of least resistance here?',
    'Which color feels like the choice nobody would question?',
    'Which color represents staying exactly where you are?',
    'Which color feels like the version of this with the fewest surprises?',
  ],
  risk: [
    'Which color feels like the biggest risk in this situation?',
    'Which color represents the option that scares you a little?',
    'Which color feels like the leap, not the ledge?',
    'Which color feels like the choice you would only make once?',
    'Which color represents the version of this with no safety net?',
    'Which color feels like betting on yourself?',
  ],
  pressure: [
    'Which color represents what others might expect you to do?',
    'Which color feels like the opinion of someone you respect?',
    'Which color captures the pressure coming from outside yourself?',
    'Which color feels like the voice of someone you are trying not to disappoint?',
    'Which color represents the choice that would look right to everyone else?',
    'Which color feels like the expectation you did not choose for yourself?',
  ],
  desire: [
    'Which color captures the outcome you truly, secretly desire?',
    'If no one else could see your answer, which color would you pick?',
    'Which color feels like the ending you are quietly hoping for?',
    'Which color represents what you would choose if nothing could go wrong?',
    'Which color feels like the version of this you actually want, underneath it all?',
    'Which color captures the outcome you would celebrate the most?',
  ],
};

function buildDecisionRounds(): Round[] {
  const order = shuffle(Object.keys(DECISION_PROMPT_BANK) as DecisionStepKind[]);
  return order.map((stepKind, i) => {
    const prompt = pickRandom(DECISION_PROMPT_BANK[stepKind], 1)[0];
    const options = pickRandom(COLOR_LIST, OPTIONS_PER_ROUND).map(c => c.id);
    return { id: `d${i}-${stepKind}`, prompt, options, stepKind };
  });
}

export function generateDecisionRounds(): Round[] {
  let rounds = buildDecisionRounds();
  let attempts = 0;
  while (signatureOf(rounds) === lastDecisionSignature && attempts < 5) {
    rounds = buildDecisionRounds();
    attempts += 1;
  }
  lastDecisionSignature = signatureOf(rounds);
  return rounds;
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

// ─── Per-color archetype system ───────────────────────────────────────────────
// Each of the 175 colors gets its own unique archetype title derived from the
// color's primary keyword + a deterministic role word (stable per color id).
// Descriptions are also color-specific, incorporating both the dominant color
// and a secondary color when two different colors surface during the quiz.

/** Deterministic non-negative hash of a string (djb2 variant). */
function strHash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return h >>> 0;
}

const ARCHETYPE_ROLES: Record<ColorFamily, string[]> = {
  warm:    ['Pioneer', 'Catalyst', 'Force', 'Igniter', 'Trailblazer', 'Challenger', 'Driver', 'Spark'],
  cool:    ['Sage', 'Observer', 'Visionary', 'Anchor', 'Navigator', 'Strategist', 'Compass', 'Architect'],
  growth:  ['Cultivator', 'Nurturer', 'Steward', 'Builder', 'Keeper', 'Gardener', 'Grower', 'Tender'],
  mystic:  ['Seeker', 'Dreamer', 'Oracle', 'Weaver', 'Mystic', 'Alchemist', 'Wanderer', 'Vessel'],
  neutral: ['Cartographer', 'Witness', 'Purist', 'Keeper', 'Pilgrim', 'Archivist', 'Curator', 'Arbiter'],
};

function archetypeTitle(color: ColorTrait): string {
  const roles = ARCHETYPE_ROLES[color.family];
  const role  = roles[strHash(color.id) % roles.length];
  return `The ${color.keywords[0]} ${role}`;
}

const FAMILY_DESC: Record<ColorFamily, (p: ColorTrait, s?: ColorTrait) => string> = {
  warm: (p, s) => {
    const k1 = p.keywords[0].toLowerCase();
    const k2 = p.keywords[1].toLowerCase();
    let d = `Your choices kept returning to ${p.name} — a color built for ${k1} and ${k2}. Right now you are not watching from the sidelines; you are in the thick of it, driven by feeling rather than waiting for certainty.`;
    if (s && s.id !== p.id)
      d += ` The thread of ${s.name.toLowerCase()} woven through your other choices adds a note of ${s.keywords[0].toLowerCase()}, tempering that fire with something more considered.`;
    return d;
  },
  cool: (p, s) => {
    const k1 = p.keywords[0].toLowerCase();
    const k2 = p.keywords[1].toLowerCase();
    let d = `${p.name} kept surfacing in your choices — a signal of ${k1} and ${k2}. You are in a phase of reading the room, processing before acting, and trusting your own judgment over outside noise.`;
    if (s && s.id !== p.id)
      d += ` A secondary pull toward ${s.name.toLowerCase()} suggests ${s.keywords[0].toLowerCase()} is also present — a reminder that still water can run deep.`;
    return d;
  },
  growth: (p, s) => {
    const k1 = p.keywords[0].toLowerCase();
    const k2 = p.keywords[1].toLowerCase();
    let d = `${p.name} emerged as your anchor — a color rooted in ${k1} and ${k2}. You are building something slowly and deliberately, less interested in speed than in whether what you create will last.`;
    if (s && s.id !== p.id)
      d += ` Your secondary affinity for ${s.name.toLowerCase()} introduces ${s.keywords[0].toLowerCase()} into the mix, suggesting the ground you are tending is richer than it first appears.`;
    return d;
  },
  mystic: (p, s) => {
    const k1 = p.keywords[0].toLowerCase();
    const k2 = p.keywords[1].toLowerCase();
    let d = `Your instincts kept circling back to ${p.name} — a color of ${k1} and hidden ${k2}. You are currently operating from a deeper layer of yourself: imagining what is not yet visible, and trusting what cannot quite be explained.`;
    if (s && s.id !== p.id)
      d += ` The presence of ${s.name.toLowerCase()} adds ${s.keywords[0].toLowerCase()} to your picture — a glimpse of something you are still reaching toward.`;
    return d;
  },
  neutral: (p, s) => {
    const k1 = p.keywords[0].toLowerCase();
    const k2 = p.keywords[1].toLowerCase();
    let d = `${p.name} defined your choices — a color of ${k1} and ${k2}. You are stripping things back right now: removing what does not serve, creating space, and finding that the clearest thoughts arrive when there is less clutter around them.`;
    if (s && s.id !== p.id)
      d += ` A secondary lean toward ${s.name.toLowerCase()} hints at ${s.keywords[0].toLowerCase()} underneath that calm exterior — something you have not quite set down yet.`;
    return d;
  },
};

export function analyzePersonality(rounds: Round[], choices: string[]): {
  title: string;
  description: string;
  dominantColors: ColorTrait[];
  primaryColorId: string;
} {
  const avoidIndex = rounds.findIndex(r => r.kind === 'avoid');
  const leftBehind = avoidIndex >= 0 ? choices[avoidIndex] : undefined;
  const positiveChoices = choices.filter((_, i) => i !== avoidIndex);

  const counts: Record<string, number> = {};
  positiveChoices.forEach(c => { counts[c] = (counts[c] || 0) + 1; });

  const sortedColors = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const primaryId   = sortedColors[0];
  const secondaryId = sortedColors[1];

  const primaryColor   = COLORS[primaryId];
  const secondaryColor = secondaryId ? COLORS[secondaryId] : undefined;

  const domColors = [primaryColor, secondaryColor]
    .filter((v): v is ColorTrait => Boolean(v))
    .filter((v, i, a) => a.indexOf(v) === i);

  const title = archetypeTitle(primaryColor);
  const family: ColorFamily = primaryColor?.family ?? 'neutral';
  let description = FAMILY_DESC[family](primaryColor, secondaryColor);

  if (leftBehind && COLORS[leftBehind]) {
    const lb = COLORS[leftBehind];
    description += ` Interestingly, you chose to leave ${lb.name.toLowerCase()} behind, suggesting you are actively moving away from feelings of ${lb.keywords[0].toLowerCase()} or ${lb.keywords[1].toLowerCase()}.`;
  }

  return { title, description, dominantColors: domColors, primaryColorId: primaryId };
}

export function analyzeDecision(rounds: Round[], choices: string[]): { insights: { step: string; color: ColorTrait; text: string }[] } {
  const order: DecisionStepKind[] = ['conflict', 'safe', 'risk', 'pressure', 'desire'];

  const insights = order
    .map(stepKind => {
      const idx = rounds.findIndex(r => r.stepKind === stepKind);
      if (idx === -1) return null;
      const color = COLORS[choices[idx]];
      if (!color) return null;
      return {
        step: DECISION_STEP_LABELS[stepKind],
        color,
        text: `Your instinct here is ${color.name.toLowerCase()}. ${color.decisionMeaning}`,
      };
    })
    .filter((v): v is { step: string; color: ColorTrait; text: string } => v !== null);

  return { insights };
}
